import { useMemo, useState } from 'react';
import { MIN_CLIP_MS, SEARCH_DEBOUNCE_MS } from '../utils/constants';
import { adjacentActiveDay } from '../utils/calendar';
import { buildLmTripRuns, findRunAt, fitRangeInside, pickActiveRun } from '../utils/lmTrips';
import {
  canShiftWindow,
  centerRangeOn,
  defaultRange,
  getTripBounds,
  getWindow,
  moveHandle,
  setRangeEdge,
  shiftWindow,
  validateRange,
} from '../utils/window';
import { useActivityDays } from './useActivityDays';
import { useCreateClipRequest } from './useCreateClipRequest';
import { useDebouncedCallback } from './useDebouncedCallback';
import { useInitialUnit } from './useInitialUnit';
import { usePrivateClipPermission } from './usePrivateClipPermission';
import { useTrailWindow } from './useTrailWindow';
import { useTripsOfDay } from './useTripsOfDay';
import { useVideoUnits } from './useVideoUnits';

const EMPTY_SELECTION = { dayKey: null, tripId: null, windowStart: null, nowMs: null, range: null };

export const useRequestVideoFlow = ({ initialCameraDeviceId, hideUnitSelector, onSubmitted }) => {
  const [search, setSearch] = useState('');
  const [pickedUnit, setPickedUnit] = useState(null);
  const [selection, setSelection] = useState(EMPTY_SELECTION);
  const [options, setOptions] = useState({ isPrivate: false });

  const canUsePrivate = usePrivateClipPermission();
  const unitList = useVideoUnits({ search, enabled: !hideUnitSelector });
  const initial = useInitialUnit(initialCameraDeviceId);
  const unit = pickedUnit || initial.unit;
  const cameraDeviceId = unit?.cameraDeviceId;
  const activityUnitId = unit?.activityUnitId;
  const timeZone = unit?.timeZone;

  const activity = useActivityDays({ activityUnitId, timeZone });
  const { days } = activity;
  let dayKey = days[days.length - 1] || null;
  if (days.includes(selection.dayKey)) dayKey = selection.dayKey;
  const tripsQuery = useTripsOfDay({ activityUnitId, dayKey, timeZone });
  const trip = tripsQuery.trips.find((item) => item.id === selection.tripId) || null;

  const { bounds, window } = useMemo(() => {
    if (!trip) return { bounds: null, window: null };
    const tripBounds = getTripBounds(trip, selection.nowMs);
    return { bounds: tripBounds, window: getWindow(selection.windowStart, tripBounds) };
  }, [trip, selection.nowMs, selection.windowStart]);
  const trail = useTrailWindow({ activityUnitId, window });
  const clip = useCreateClipRequest({ onSubmitted });

  // The clip must lie inside one LightMetrics trip: the range is kept inside the run it starts in.
  const runs = useMemo(() => (window ? buildLmTripRuns(trail.events, window) : []), [trail.events, window]);
  const activeRun = useMemo(() => {
    if (!selection.range || runs.length === 0) return null;
    return pickActiveRun(runs, selection.range);
  }, [runs, selection.range]);
  const range = useMemo(() => {
    if (!selection.range || !activeRun) return selection.range;
    return fitRangeInside(selection.range, activeRun);
  }, [selection.range, activeRun]);
  let rangeBounds = window;
  if (activeRun) rangeBounds = { start: activeRun.start, end: activeRun.end };

  let rangeError = 'missing';
  if (window) rangeError = validateRange(range, rangeBounds);
  const canSubmit = Boolean(trip) && !rangeError && !clip.isSending && !trail.isLoading;

  const startSearch = useDebouncedCallback(setSearch, SEARCH_DEBOUNCE_MS);

  const pickUnit = (next) => {
    setPickedUnit(next);
    setSelection(EMPTY_SELECTION);
    clip.reset();
  };

  const pickDay = (nextDay) => {
    setSelection({ ...EMPTY_SELECTION, dayKey: nextDay });
  };

  const stepDay = (direction) => {
    const next = adjacentActiveDay(days, dayKey, direction);
    if (next) pickDay(next);
  };

  const placeWindow = (startMs, tripItem, nowMs) => {
    const nextWindow = getWindow(startMs, getTripBounds(tripItem, nowMs));
    setSelection((prev) => ({
      ...prev,
      tripId: tripItem.id,
      windowStart: nextWindow.start,
      nowMs,
      range: defaultRange(nextWindow),
    }));
  };

  const pickTrip = (tripItem) => placeWindow(tripItem.startMs, tripItem, Date.now());

  const moveWindow = (direction) => {
    if (!trip || !canShiftWindow(window, direction, bounds)) return;
    placeWindow(shiftWindow(window, direction, bounds).start, trip, selection.nowMs);
  };

  const updateRange = (nextRange) => setSelection((prev) => ({ ...prev, range: nextRange }));

  const runBounds = (run) => ({ start: run.start, end: run.end });

  const rangeActions = {
    nudge: (handle, deltaMs) => updateRange(moveHandle(range, handle, deltaMs, rangeBounds)),
    setEdge: (edge, ms) => updateRange(setRangeEdge(range, edge, ms, rangeBounds)),
    centerOn: (ms) => {
      const run = findRunAt(runs, ms) || activeRun;
      updateRange(centerRangeOn(ms, range, run ? runBounds(run) : window));
    },
    // Moves the clip to the LightMetrics trip under `ms`, keeping its length.
    jumpTo: (ms) => {
      const run = findRunAt(runs, ms) || activeRun;
      // A trip shorter than the minimum clip cannot hold one: stay where we are.
      if (run && run.end - run.start >= MIN_CLIP_MS) {
        updateRange(fitRangeInside({ start: ms, end: ms + (range.end - range.start) }, run));
      }
    },
  };

  const submit = () => {
    if (!canSubmit) return;
    clip.submit({
      cameraDeviceId,
      startMs: range.start,
      endMs: range.end,
      isPrivate: canUsePrivate && options.isPrivate,
    });
  };

  return {
    unit,
    unitList,
    initial,
    search,
    startSearch,
    pickUnit,
    activity,
    days,
    dayKey,
    pickDay,
    stepDay,
    hasPrevDay: Boolean(dayKey && adjacentActiveDay(days, dayKey, -1)),
    hasNextDay: Boolean(dayKey && adjacentActiveDay(days, dayKey, 1)),
    tripsQuery,
    trip,
    pickTrip,
    window,
    bounds,
    moveWindow,
    canMoveWindow: (direction) => Boolean(window) && canShiftWindow(window, direction, bounds),
    trail,
    lmRuns: runs,
    activeRun,
    range,
    rangeActions,
    rangeError,
    options,
    setOptions: (patch) => setOptions((prev) => ({ ...prev, ...patch })),
    canUsePrivate,
    clip,
    canSubmit,
    submit,
  };
};
